import React, { useEffect, useState, useMemo } from 'react';
import { Server, HardDrive, Database, Activity, ChevronUp, ChevronDown, Search, ChevronsUpDown } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { Customer } from '../types';
import { formatBytes, getProgressBarColor } from '../utils/formatters';
import { TEXT } from '../constants/localization';
import { API_ENDPOINTS } from '../config/api';
import { getDetailedErrorMessage, DetailedError } from '../utils/errorHandler';
import DetailedErrorDisplay from './DetailedErrorDisplay';

type SortField = 'station_code' | 'total' | 'used' | 'usage_percent';
type SortDirection = 'asc' | 'desc';

const AllCustomersCard: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DetailedError | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('station_code');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchAllCustomers();
    const interval = setInterval(fetchAllCustomers, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllCustomers = async () => {
    try {
      const response = await apiClient.get<Customer[]>(API_ENDPOINTS.CUSTOMERS);
      setCustomers(response.data);
      setError(null);
    } catch (err) {
      const detailedError = getDetailedErrorMessage(err);
      setError(detailedError);
      console.error('Customers fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField === field) {
      return sortDirection === 'asc'
        ? <ChevronUp className="inline h-4 w-4 ml-1" />
        : <ChevronDown className="inline h-4 w-4 ml-1" />;
    }
    return <ChevronsUpDown className="inline h-4 w-4 ml-1 opacity-40" />;
  };

  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = customers.filter(customer =>
      customer.station_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'station_code':
          aValue = a.station_code;
          bValue = b.station_code;
          break;
        case 'total':
          aValue = a.total;
          bValue = b.total;
          break;
        case 'used':
          aValue = a.used;
          bValue = b.used;
          break;
        case 'usage_percent':
          aValue = typeof a.usage_percent === 'number'
            ? a.usage_percent
            : parseFloat(a.usage_percent as any) || 0;
          bValue = typeof b.usage_percent === 'number'
            ? b.usage_percent
            : parseFloat(b.usage_percent as any) || 0;
          break;
        default:
          aValue = a.station_code;
          bValue = b.station_code;
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [customers, searchTerm, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
          <Database className="h-6 w-6 mr-2 text-blue-600" />
          {TEXT.ALL_CUSTOMERS_OVERVIEW}
        </h2>
        <DetailedErrorDisplay
          error={error}
          onRetry={fetchAllCustomers}
          retryLabel="重新載入客戶資料"
        />
      </div>
    );
  }

  if (!customers.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
          <Database className="h-6 w-6 mr-2 text-blue-600" />
          {TEXT.ALL_CUSTOMERS_OVERVIEW}
        </h2>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {TEXT.NO_DATA_AVAILABLE}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
          <Database className="h-6 w-6 mr-2 text-blue-600" />
          {TEXT.ALL_CUSTOMERS_OVERVIEW} ({filteredAndSortedCustomers.length} / {customers.length} {TEXT.TOTAL})
        </h2>
        <div className="relative">
          <input
            type="text"
            placeholder={TEXT.SEARCH_CUSTOMER_ID}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>
      </div>

      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleSort('station_code')}
              >
                <div className="flex items-center">
                  {TEXT.CUSTOMER_ID}
                  <SortIcon field="station_code" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <Server className="h-4 w-4 mr-1" />
                  {TEXT.STORAGE_SERVERS}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div className="flex items-center">
                  <Activity className="h-4 w-4 mr-1" />
                  {TEXT.CURRENT_SERVER}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleSort('total')}
              >
                <div className="flex items-center">
                  <Database className="h-4 w-4 mr-1" />
                  {TEXT.TOTAL_SPACE_TABLE}
                  <SortIcon field="total" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleSort('used')}
              >
                <div className="flex items-center">
                  <HardDrive className="h-4 w-4 mr-1" />
                  {TEXT.USED_SPACE}
                  <SortIcon field="used" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleSort('usage_percent')}
              >
                <div className="flex items-center">
                  Usage
                  <SortIcon field="usage_percent" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAndSortedCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {TEXT.NO_MATCHING_RESULTS}
                </td>
              </tr>
            ) : (
              filteredAndSortedCustomers.map((customer) => {
                const usagePercent = typeof customer.usage_percent === 'number'
                  ? customer.usage_percent
                  : parseFloat(customer.usage_percent as any) || 0;
                return (
                  <tr key={customer.station_code} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {customer.station_code}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex flex-wrap gap-1">
                        {Array.isArray(customer.partition_server_num)
                          ? customer.partition_server_num.map(server => (
                              <span key={server} className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                {server}
                              </span>
                            ))
                          : <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                              {customer.partition_server_num}
                            </span>
                        }
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        Server {customer.current_server_num}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      <div>
                        <div className="font-medium">{formatBytes(parseInt(customer.total.toString()))}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">{parseInt(customer.total.toString()).toLocaleString()} bytes</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      <div>
                        <div className="font-medium">{formatBytes(parseInt(customer.used.toString()))}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">{parseInt(customer.used.toString()).toLocaleString()} bytes</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="w-24">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor(usagePercent)}`}
                              style={{ width: `${Math.min(usagePercent, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium ${
                            usagePercent >= 90 ? 'text-red-600 dark:text-red-400' :
                            usagePercent >= 75 ? 'text-orange-600 dark:text-orange-400' :
                            usagePercent >= 60 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                          }`}>
                            {Number(usagePercent).toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatBytes(parseInt((customer.available || 0).toString()))} {TEXT.AVAILABLE}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">{TEXT.TOTAL_CUSTOMERS}:</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredAndSortedCustomers.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">{TEXT.TOTAL_SPACE}:</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatBytes(filteredAndSortedCustomers.reduce((sum, c) => sum + (parseInt(c.total.toString()) || 0), 0))}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">{TEXT.TOTAL_USED}:</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatBytes(filteredAndSortedCustomers.reduce((sum, c) => sum + (parseInt(c.used.toString()) || 0), 0))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCustomersCard;